import {
  type AndGroup,
  type BaseFilter,
  type CompatibleFilters,
  type OrGroup,
  type DatasetPayload,
  type ScopeColumn,
  columnData
} from 'common'

/**
 * Checks if a group is an AndGroup
 * @param group - The group to check
 * @returns True if the group is an AndGroup, false otherwise
 */
export function isAndGroup(group: AndGroup | OrGroup | BaseFilter): group is AndGroup {
  return 'and' in group
}

/**
 * Checks if a group is an OrGroup
 * @param group - The group to check
 * @returns True if the group is an OrGroup, false otherwise
 */
export function isOrGroup(group: AndGroup | OrGroup | BaseFilter): group is OrGroup {
  return 'or' in group
}

/**
 * Checks if a filter is a BaseFilter
 * @param filter - The filter to check
 * @returns True if the filter is a BaseFilter, false otherwise
 */
export function isBaseFilter(filter: AndGroup | OrGroup | BaseFilter): filter is BaseFilter {
  return 'column' in filter
}

/**
 * Sanitizes a dataset by making sure that all columns are valid. Incorrect ones are removed on the fly.
 * @param columns - The columns to sanitize
 * @returns The sanitized columns
 */
function sanitizeColumns(columns: ScopeColumn[]): ScopeColumn[] {
  const sanitizedColumns = columns.filter((column) => {
    const colData = columnData[column.column]
    return colData !== undefined
  })
  if (sanitizedColumns.length !== columns.length) {
    console.error(
      `Caught an invalid column: ${columns
        .filter((c) => !sanitizedColumns.includes(c))
        .map((c) => c.column)
        .join(', ')}`
    )
  }
  return sanitizedColumns
}

function sanitizeFilter(
  filter: AndGroup | OrGroup | BaseFilter
): AndGroup | OrGroup | BaseFilter | null {
  if (isBaseFilter(filter)) {
    if (columnData[filter.column]) {
      return filter
    }
    console.error(`Caught an invalid filter: ${JSON.stringify(filter)}`)
    return null
  }
  if (isAndGroup(filter)) {
    const cleanedAnd = filter.and.map(sanitizeFilter).filter((f) => f !== null)
    if (cleanedAnd.length === 0) {
      return null
    }
    return { and: cleanedAnd }
  }
  if (isOrGroup(filter)) {
    const cleanedOr = filter.or.map(sanitizeFilter).filter((f) => f !== null)
    if (cleanedOr.length === 0) {
      return null
    }
    return { or: cleanedOr }
  }
  return null
}

/**
 * Sanitizes a filter by making sure they are all valid. Incorrect ones are removed on the fly.
 * @param filters - The filters to sanitize
 * @throws {Error} When an unknown filter type is encountered
 * @returns The sanitized filters
 */
function sanitizeFilters(filters: CompatibleFilters): CompatibleFilters {
  if (Array.isArray(filters)) {
    return { and: filters.map(sanitizeFilter).filter((f) => f !== null) }
  }
  if (isAndGroup(filters)) {
    return { and: filters.and.map(sanitizeFilter).filter((f) => f !== null) }
  }
  if (isOrGroup(filters)) {
    return { or: filters.or.map(sanitizeFilter).filter((f) => f !== null) }
  }
  throw new Error(`Unknown filter: ${JSON.stringify(filters)}`)
}

/**
 * Sanitizes a dataset by making sure that all filters and columns are valid
 * @param dataset - The dataset to sanitize
 * @returns The sanitized dataset
 */
export function sanitizeDataset(dataset: DatasetPayload): DatasetPayload {
  return {
    scopes: sanitizeColumns(dataset.scopes),
    filters: sanitizeFilters(dataset.filters),
    limit: dataset.limit
  }
}

/**
 * Converts a filter to a list of nodes
 * @param filter - The filter to convert
 * @returns A list of nodes
 */
const filterToList = (filter: AndGroup | OrGroup | BaseFilter): BaseFilter[] => {
  if (isOrGroup(filter)) {
    return filter.or.map(filterToList).flat()
  } else if (isAndGroup(filter)) {
    return filter.and.map(filterToList).flat()
  } else if (isBaseFilter(filter)) {
    return [filter]
  }
  throw new Error(`Unknown filter: ${filter}`)
}

/**
 * Converts a filter to a list of nodes
 * @param filters - The filter to convert
 * @returns A list of nodes
 */
export const parentFilterToList = (filters: CompatibleFilters): BaseFilter[] => {
  if (Array.isArray(filters)) {
    return filters.map(filterToList).flat()
  }

  if (isOrGroup(filters)) {
    return filters.or.map(filterToList).flat()
  } else if (isAndGroup(filters)) {
    return filters.and.map(filterToList).flat()
  }
  throw new Error(`Unknown filter: ${filters}`)
}
